<template>
  <div
    id="navigation-container"
    class="flex flex-row"
  >
    <div
      id="main-sidebar"
      ref="sidebarRef"
      class="h-full group"
    >
      <!-- Overlay/mask for mobile -->
      <div
        v-if="!isCollapsed && isMobile"
        class="fixed inset-0 bg-black/50 z-[30] md:hidden"
        @click="toggleSidebar"
      />

      <!-- Mobile Nav button section -->
      <SidebarMobileNav
        :is-collapsed="isCollapsed"
        :is-mobile="isMobile"
        @toggle="toggleSidebar"
      />

      <!-- Main sidebar container -->
      <div
        :class="[
          'h-screen min-h-full fixed md:relative w-16 z-[35] bg-surface-light flex flex-col shadow-md transition-all duration-150',
          // Mobile: hide when collapsed, show when expanded
          isMobile && isCollapsed ? '-left-full' : 'left-0',
          // Mobile: full width when expanded with top offset for mobile nav button
          { 'w-64 sm:w-64 top-10': !isCollapsed && isMobile },
          // Desktop: cursor styles for collapse button
          !isMobile && showCollapseButton ? 'cursor-e-resize rtl:cursor-w-resize' : '',
          !isMobile && !showCollapseButton ? 'cursor-w-resize rtl:cursor-e-resize' : '',
        ]"
        @click="handleSidebarClick"
      >
        <!-- Logo section -->
        <SidebarLogo :show-collapse-button="showCollapseButton" />

        <!-- Navigation items -->
        <nav

          class="flex-1 mt-4 md:mt-0 overflow-y-auto"
        >
          <ul
            ref="navItemsRef"
            class="flex flex-col"
          >
            <template
              v-for="item in menuItems"
              :key="item.key"
            >
              <SidebarNavItem
                :item="item"
                :is-mobile="isMobile"
                :is-collapsed="isCollapsed"
                :is-active="checkActiveResource(item.key)"
                :activation-state-enabled="activationStateEnabled"
                :is-feature-enabled="isFeatureEnabled(item.featureKeys)"
                @click="handleNavClick"
              />

              <!-- Secondary nav items (mobile only) -->
              <template v-if="isMobile && !isCollapsed && getSecondaryItems(item.key).length > 0">
                <li
                  v-for="(navItem, index) in getSecondaryItems(item.key)"
                  :key="`${item.key}-secondary-${index}`"
                  class="md:hidden bg-surface/30"
                >
                  <button
                    class="w-full flex items-center py-3 pl-12 pr-5 text-sm text-secondary hover:bg-surface cursor-pointer transition-colors relative"
                    :class="{ 'text-brand font-medium': isSecondaryItemActive(navItem.path) }"
                    @click="handleSecondaryNavClick(navItem.path)"
                  >
                    <!-- Indent indicator -->
                    <span
                      class="absolute left-8 w-0.5 h-3 bg-surface-dark"
                      :class="{ 'bg-brand': isSecondaryItemActive(navItem.path) }"
                    />
                    <span class="text-sm truncate">
                      {{ navItem.label }}
                    </span>
                  </button>
                </li>
              </template>
            </template>
          </ul>
        </nav>

        <!-- Sidebar tools: OptIn Panel, Help, Account -->
        <SidebarTools
          ref="sidebarToolsComponentRef"
          :is-mobile="isMobile"
          :is-collapsed="isCollapsed"
        />
      </div>
    </div>

    <!-- SecondaryNav component -->
    <SecondaryNav />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch, reactive, useTemplateRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useMouseInElement } from '@vueuse/core'
import SidebarMobileNav from './SidebarMobileNav.vue'
import SidebarLogo from './SidebarLogo.vue'
import SidebarNavItem from './SidebarNavItem.vue'
import SidebarTools from './SidebarTools.vue'
import SecondaryNav from './SecondaryNav.vue'

// Store and router
const store = useStore()
const route = useRoute()
const router = useRouter()

// State
const isCollapsed = ref(true)
const isMobile = ref(false)

// Sidebar and nav items refs
const sidebarRef = useTemplateRef('sidebarRef')
const navItemsRef = useTemplateRef('navItemsRef')
const sidebarToolsComponentRef = ref<InstanceType<typeof SidebarTools> | null>(null)

const mouseSidebar = reactive(useMouseInElement(sidebarRef))
const mouseNavItems = reactive(useMouseInElement(navItemsRef))

// Get the actual DOM ref from the component
const mouseSidebarTools = reactive(useMouseInElement(computed(() => sidebarToolsComponentRef.value?.sidebarToolsRef)))

const hasMousedOverOnce = ref(false)
const hasMousedOut = ref(false)

// Watch for mouse out/in cycle to enable collapse button functionality
watch(() => mouseSidebar.isOutside, (isOutside) => {
  // First, track when mouse leaves
  if (isOutside && !hasMousedOut.value) {
    hasMousedOut.value = true
  }
  // Then, after it has left once, track when it comes back in
  if (!isOutside && hasMousedOut.value && !hasMousedOverOnce.value) {
    hasMousedOverOnce.value = true
  }
})

const showCollapseButton = computed(() =>
  hasMousedOverOnce.value && !isSecondaryNavOpen.value
    ? !mouseSidebar.isOutside && mouseNavItems.isOutside && mouseSidebarTools.isOutside
    : false,
)

// Menu items
interface MenuItem {
  name: string
  key: string
  path: string
  iconType: string
  featureKeys: string[]
  tooltip?: string
}

const menuItems = computed<MenuItem[]>(() => {
  const items: MenuItem[] = [
    // This is still being designed, so we are hiding it for now. It will be re-enabled when the new home page / dash is ready.
    // {
    //   name: 'Home',
    //   key: 'dashboard',
    //   path: 'dashboard',
    //   iconType: 'navigation-home',
    //   featureKeys: ['dashboard'],
    // },
    {
      name: 'Data capture',
      key: 'campaigns',
      path: 'campaigns',
      iconType: 'navigation-data-capture',
      featureKeys: ['campaigns'],
    },
    {
      name: 'Events',
      key: 'events',
      path: 'events',
      iconType: 'navigation-events',
      featureKeys: ['events'],
    },
    {
      name: 'Audience',
      key: 'audience',
      path: 'audience',
      iconType: 'navigation-audience',
      featureKeys: ['audience'],
    },
    {
      name: 'Insights',
      key: 'insights',
      path: 'insights',
      iconType: 'navigation-insights',
      featureKeys: ['insights'],
    },
    {
      name: 'Messaging',
      key: 'message-center',
      // NOT READY FOR RELEASE - keep pointing at the AM2 message centre (via
      // the hybrid-routing redirect) until arShowMessagingInSidebar is
      // flipped on, same gating precedent as Planner below.
      path: showMessagingInSidebar.value ? 'messages' : 'message-center/messages',
      iconType: 'navigation-messaging',
      featureKeys: ['messages'],
    },
  ]

  if (arEnableAutomation.value) {
    items.push({
      name: 'Automation',
      key: 'automation',
      path: 'automation',
      iconType: 'navigation-automation',
      featureKeys: [],
    })
  }

  if (config.arShowPlannerInSidebar) {
    items.push({
      name: 'Planner',
      key: 'planner',
      path: 'planner',
      iconType: 'calendar-days',
      featureKeys: [],
      tooltip: 'DEV ONLY',
    })
  }

  return items
})

// Static mapping of parent pages to their secondary nav items
const secondaryNavConfig = computed<Record<string, Array<{ label: string; path: string }>>>(() => ({
  audience: [
    { label: 'Contacts', path: '/audience' },
    { label: 'My tags', path: '/audience/tags' },
    { label: 'Saved filters', path: '/audience/filters' },
  ],
  campaigns: [
    { label: 'Data capture', path: '/campaigns' },
  ],
  events: [
    { label: 'Upcoming events', path: '/events' },
    { label: 'Past events', path: '/events/past' },
  ],
  // NOT READY FOR RELEASE - only show the in-progress AM3 message-centre
  // secondary nav once arShowMessagingInSidebar is on; see menuItems above.
  ...(showMessagingInSidebar.value
    ? {
        'message-center': [
          { label: 'Messages', path: '/messages' },
          { label: 'Lists', path: '/messages/lists' },
          { label: 'Templates', path: '/messages/templates' },
        ],
      }
    : {}),
}))

// Computed properties
const isSecondaryNavOpen = computed(() => store.getters['secondaryNavigationModule/isSecondaryNavigationVisible'] || false)
const secondaryNavTitle = computed(() => store.getters['secondaryNavigationModule/secondaryNavigationTitle'] || '')
const config = useRuntimeConfig().public || {}

// Config values
const activationStateEnabled = computed(() => config.arEnableActivationState)
const arEnableAutomation = computed(() => config.arEnableAutomation)
const showMessagingInSidebar = computed(() => config.arShowMessagingInSidebar)

// Feature flags
const isFeatureEnabled = (keys: string[]) => store.getters['authModule/isFeatureEnabled'](keys)

// Methods
const toggleSidebar = () => {
  isCollapsed.value = !isCollapsed.value
}

const toggleSecondaryNav = () => {
  const newValue = !isSecondaryNavOpen.value
  store.commit('secondaryNavigationModule/SET_IS_SECONDARY_NAVIGATION_VISIBLE', newValue)
  try {
    localStorage.setItem('secondaryNavVisible', JSON.stringify(newValue))
  }
  catch (e) {
    console.warn('Failed to save secondary nav state to localStorage:', e)
  }
}

const handleNavClick = (item: any) => {
  const sectionDirectories = getSecondaryItems(item.key).map(navItem => navItem.path)
  const isOnSectionDirectory = sectionDirectories.includes(route.path)

  if (isOnSectionDirectory && secondaryNavTitle.value) {
    toggleSecondaryNav()
    return
  }

  navigateTo(`/${item.path}`)
  if (isMobile.value) {
    isCollapsed.value = true
  }
}

const handleSidebarClick = (event: MouseEvent) => {
  // Only toggle if there's a secondary nav and click wasn't on an interactive element
  const target = event.target as HTMLElement | null
  if (secondaryNavTitle.value && target && !target.closest('button, a, [role="button"]')) {
    toggleSecondaryNav()
  }
}

const checkActiveResource = (key: string) => {
  return route.path.indexOf(`/${key}`) === 0
}

const getSecondaryItems = (itemKey: string) => {
  // Return secondary nav items for this menu item
  return secondaryNavConfig.value[itemKey] || []
}

const isSecondaryItemActive = (path: string) => {
  // Check if the current route matches this secondary nav item path
  return route.path === path
}

const navigateTo = (path: string) => {
  router.push({ path }).catch((error) => {
    // Ignore navigation duplication errors
    if (error.name !== 'NavigationDuplicated') {
      throw error
    }
  })
}

const handleSecondaryNavClick = (path: string) => {
  navigateTo(path)
  // Close mobile sidebar after navigation
  if (isMobile.value) {
    isCollapsed.value = true
  }
}

// Check for mobile view on mount and window resize
const checkMobileView = () => {
  isMobile.value = window.innerWidth < 768 // md breakpoint
}

onMounted(() => {
  if (config.arEnableIntercomButton && window.Intercom) {
    window.Intercom('boot')
  }
  checkMobileView()
  window.addEventListener('resize', checkMobileView)

  // Check tasks if needed
  store.dispatch('promoterTasksModule/START_POLLING_PENDING_TASKS')
})

onUnmounted(() => {
  store.dispatch('promoterTasksModule/STOP_POLLING_PENDING_TASKS')
  window.removeEventListener('resize', checkMobileView)
})
</script>

<style scoped>
:deep(.navbar-item:hover .navbar-icon svg) {
  color: var(--font-colour-brand);
}
</style>
